import React, { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check, MessageCircle, ArrowRight } from "lucide-react";
import * as Icons from "lucide-react";
import { SERVICE_DETAILS } from "../data/serviceDetails";
import { useI18n } from "../i18n";
import NavBar from "../components/NavBar";
import Footer from "../components/Footer";
import FloatingWhatsApp from "../components/FloatingWhatsApp";
import ServiceVisual from "../components/ServiceVisual";
import { CmsImageSlideshow as CmsImage, CmsMedia } from "../lib/images";
import { api } from "../lib/api";
import { WHATSAPP_ENABLED, waProps, waLabel } from "../lib/whatsapp";

export default function ServiceDetail() {
  const { slug } = useParams();
  const { pick, t } = useI18n();
  const [content, setContent] = useState(null);
  const detail = SERVICE_DETAILS[slug];

  useEffect(() => {
    api.get("/content").then(r => setContent(r.data?.data || null)).catch(() => {});
    api.post("/track/visit", { path: `/servicios/${slug}` }).catch(() => {});
    window.scrollTo(0, 0);
  }, [slug]);

  if (!detail) return <Navigate to="/" replace />;

  const Icon = Icons[detail.icon] || Icons.Satellite;  const wa = content?.contact?.whatsapp || "+525555555555";

  return (
    <div className="min-h-screen text-white" data-testid="service-detail">
      <NavBar contact={content?.contact} />

      {/* Hero with banner image */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          {slug === "adultos-mayores" ? (
            <CmsImage
              slotKey="adultos_mayores.hero"
              defaultSrc={detail.image}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover opacity-60"
              loading="eager"
            />
          ) : (
            <img src={detail.image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" />
          )}
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_30%_45%,rgba(6,8,13,0.45),rgba(6,8,13,0.85)_70%,#06080D_100%)]" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#06080D]/90 via-[#06080D]/40 to-transparent" />
          <div className="absolute inset-0 grid-bg opacity-20" />
          <div className="absolute top-0 left-0 right-0 h-28 bg-gradient-to-b from-[#06080D]/85 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 h-56 sm:h-64 bg-gradient-to-b from-transparent via-[#06080D]/95 to-[#06080D]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-5 sm:px-6 md:px-10 pt-32 pb-20 sm:pt-36 sm:pb-28 md:pt-44 md:pb-32">
          <Link to="/#servicios" className="inline-flex items-center gap-2 label-mono !text-cyan-300/80 hover:!text-cyan-300 mb-8" data-testid="back-link">
            <ArrowLeft className="w-4 h-4" /> Servicios / All
          </Link>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: .6 }}
            className="max-w-3xl">
            <div className="inline-flex items-center gap-3 px-3 py-2 rounded-full border border-cyan-400/30 bg-cyan-400/10 backdrop-blur mb-6">
              <Icon className="w-4 h-4 text-cyan-300" />
              <span className="label-mono !text-cyan-300">{pick("Servicio Xega", "Xega Service", "Serviço Xega")}</span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] chrome-text drop-shadow-[0_2px_18px_rgba(0,240,255,0.18)]">
              {pick(detail.title_es, detail.title_en, detail.title_pt)}
            </h1>
            <p className="mt-5 text-base sm:text-lg text-white/75">{pick(detail.intro_es, detail.intro_en, detail.intro_pt)}</p>
            <div className="mt-7 flex flex-wrap gap-3">
              {WHATSAPP_ENABLED && (
                <a href={`https://wa.me/${wa.replace(/\D/g,'')}`} target="_blank" rel="noreferrer" className="btn-metal" data-testid="service-cta-whatsapp">
                  <MessageCircle className="w-4 h-4" /> {t("hero.cta2")}
                </a>
              )}
              <Link to="/contacto" className="btn-ghost-chrome" data-testid="service-cta-contact">
                {t("nav.contact")} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Featured representative illustration */}
      <section className="relative -mt-8 md:-mt-12 mb-4">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ duration: .7 }}>
            {slug === "adultos-mayores" ? (
              <CmsMedia
                slotKey="adultos_mayores.visual"
                defaultSrc=""
                alt=""
                className="w-full h-auto block aspect-[16/10] object-cover bg-[#06080D] rounded-2xl chrome-border"
                fallback={<ServiceVisual slug={slug} label={pick(detail.feature_caption_es, detail.feature_caption_en, detail.feature_caption_pt)} />}
              />
            ) : (
              <ServiceVisual slug={slug} label={pick(detail.feature_caption_es, detail.feature_caption_en, detail.feature_caption_pt)} />
            )}
          </motion.div>
        </div>
      </section>

      {/* Body + bullets */}
      <section className="relative py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="label-mono mb-3">/ {pick("Nuestros servicios", "Our services", "Nossos serviços")}</div>
            <h2 className="font-display text-2xl md:text-3xl chrome-text leading-tight">{pick("Plataforma sencilla y potente", "Simple, powerful platform", "Plataforma simples e potente")}</h2>
            <div className="mt-5 text-white/75 leading-relaxed space-y-4">
              {pick(detail.body_es, detail.body_en, detail.body_pt).split("\n\n").map((p, i) => (
                <p key={i} className="whitespace-pre-line">{p}</p>
              ))}
            </div>
            <div className="mt-8 glass-card chrome-border p-5 inline-flex items-start gap-3">
              <Icon className="w-5 h-5 text-cyan-300 mt-0.5 shrink-0" />
              <div>
                <div className="label-mono">/ {pick("Detalles del servicio", "Service details", "Detalhes do serviço")}</div>
                <p className="mt-1 text-sm text-white/70">
                  {pick(
                    "Conoce algunas de las características que ofrecemos en Xega, para ayudarte a administrar tus flotillas o cuidar tus vehículos.",
                    "Discover some of the features Xega offers to help you manage your fleets or take care of your vehicles.",
                    "Conheça algumas das características que oferecemos na Xega para ajudar você a gerenciar suas frotas ou cuidar dos seus veículos."
                  )}
                </p>
              </div>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="glass-card chrome-border p-6 md:p-8 sticky top-28">
              <div className="label-mono mb-3">/ {pick("Beneficios", "Benefits", "Benefícios")}</div>
              <ul className="space-y-3">
                {pick(detail.bullets_es, detail.bullets_en, detail.bullets_pt).map((b, i) => (
                  <motion.li key={i} initial={{ opacity: 0, x: -8 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
                    transition={{ duration: .35, delay: i * 0.05 }} className="flex items-start gap-3 text-sm md:text-[15px] text-white/85">
                    <span className="mt-1 w-5 h-5 rounded-md flex items-center justify-center bg-cyan-400/15 border border-cyan-400/40 shrink-0">
                      <Check className="w-3 h-3 text-cyan-300" />
                    </span>
                    <span>{b}</span>
                  </motion.li>
                ))}
              </ul>
              <a {...waProps(`https://wa.me/${wa.replace(/\D/g,'')}`)} className="btn-metal w-full mt-7" data-testid="service-card-cta">
                {WHATSAPP_ENABLED ? <><MessageCircle className="w-4 h-4" /> {t("hero.cta2")}</> : <>{waLabel(pick)} <ArrowRight className="w-4 h-4" /></>}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Editorial images block (Adultos Mayores) */}
      {slug === "adultos-mayores" && (
        <section className="relative pb-16 md:pb-24" data-testid="adultos-editorial">
          <div className="max-w-7xl mx-auto px-5 sm:px-6 md:px-10">
            {/* Tagline */}
            <motion.p
              initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative font-display leading-[1.12] tracking-tight text-center max-w-3xl mx-auto text-[1.4rem] sm:text-2xl md:text-3xl lg:text-[2.2rem]"
              data-testid="adultos-tagline"
            >
              <span
                className="chrome-text italic font-semibold"
                style={{
                  letterSpacing: '-0.005em',
                  filter: 'drop-shadow(0 2px 12px rgba(0,240,255,0.15)) drop-shadow(0 1px 0 rgba(0,0,0,0.6))',
                }}
              >
                {pick(
                  "A un botón de distancia del experto en el manejo de crisis en caso de emergencias.",
                  "One button away from a crisis-management expert when emergencies happen.",
                  "A um botão de distância do especialista em gestão de crises em caso de emergências."
                )}
              </span>
            </motion.p>

            {/* Two editable images */}
            <div className="mt-10 md:mt-14 grid md:grid-cols-2 gap-6 md:gap-10">
              <motion.div
                initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.7 }}
                className="relative"
              >
                <div className="relative rounded-2xl overflow-hidden chrome-border shadow-[0_25px_60px_-15px_rgba(0,240,255,0.15)]">
                  <CmsImage
                    slotKey="adultos_mayores.image1"
                    defaultSrc="https://images.unsplash.com/photo-1584515933487-779824d29309?q=80&w=1800&auto=format&fit=crop"
                    alt=""
                    className="w-full h-full object-cover aspect-[4/3]"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-tr from-[#06080D]/40 via-transparent to-cyan-500/10 mix-blend-multiply pointer-events-none" />
                </div>
                <div className="absolute -top-2 -left-2 w-6 h-6 border-t border-l border-cyan-400/40 pointer-events-none" />
                <div className="absolute -bottom-2 -right-2 w-6 h-6 border-b border-r border-cyan-400/40 pointer-events-none" />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
                transition={{ duration: 0.7, delay: 0.1 }}
                className="relative"
              >
                <div className="relative rounded-2xl overflow-hidden chrome-border shadow-[0_25px_60px_-15px_rgba(0,240,255,0.15)]">
                  <CmsImage
                    slotKey="adultos_mayores.image2"
                    defaultSrc="https://images.unsplash.com/photo-1516585427167-9f4af9627e6c?q=80&w=1800&auto=format&fit=crop"
                    alt=""
                    className="w-full h-full object-cover aspect-[4/3]"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-tl from-[#06080D]/45 via-transparent to-emerald-500/10 mix-blend-multiply pointer-events-none" />
                </div>
                <div className="absolute -top-2 -right-2 w-6 h-6 border-t border-r border-cyan-400/40 pointer-events-none" />
                <div className="absolute -bottom-2 -left-2 w-6 h-6 border-b border-l border-cyan-400/40 pointer-events-none" />
              </motion.div>
            </div>
          </div>
        </section>
      )}

      <Footer content={content} />
      <FloatingWhatsApp phone={content?.contact?.whatsapp} />
    </div>
  );
}
